import { AspectRatio, ContentAnchor, LogoPosition } from '@api/selections';
import { BlockComponentBaseProps, EpiImage, EpiLink } from '@api/types';
import { BlockContainer, Button, EditPanel, SmartLink, Title } from '@components';
import { Box, Stack, styled, useMediaQuery, useTheme } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';
import { getImageResizerUrl } from '@utils/images.utils';
import classnames from 'classnames';
import { useEffect } from 'react';
import './HeroBlock.css';

const HeroBlockWrapper = styled(Box)<HeroImageProps>`
  display: flex;
  align-items: stretch;
  position: relative;
  aspect-ratio: ${(p) => p.aspectratio};
  min-height: 250px;

  .hero-block-title-wrapper {
    display: flex;
    width: 100%;
  }
  .hero-block-content-wrapper {
    width: 100%;
    position: relative;
  }
  .hero-block-media {
    width: 100%;
    object-fit: cover;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -1;
  }
  img.hero-block-media {
    object-fit: cover;
    object-position: ${(p) => p.anchor} !important;
  }

  .main-title {
    transition: font-size 200ms linear;
  }
`;

const HeroBlockContent = styled('div')`
  text-align: center;
  transition: width 200ms linear;
  display: flex;
  flex-direction: column;
  padding: 10px 20px;
  width: 100%;
  height: 100%;

  ${breakpoints.up('md')} {
    padding: 40px;
  }

  text-align: center;
  justify-content: center;
  align-items: center;

  &.center {
    text-align: center;
    align-items: center;
  }

  &.left {
    text-align: left;
    align-items: flex-start;
  }

  &.right {
    text-align: right;
    align-items: flex-end;
  }

  &.top,
  &.top.center {
    justify-content: flex-start;
  }

  &.bottom,
  &.bottom.center {
    justify-content: flex-end;
  }

  h1 {
    width: 100%;
    font-size: 32px;

    ${breakpoints.up('sm')} {
      max-width: 70%;
      font-size: 42px;
    }

    ${breakpoints.up('md')} {
      width: 77%;
      max-width: 1000px;
      font-size: 56px;
    }

    ${breakpoints.up('xxl')} {
      max-width: 1300px;
      font-size: 64px;
    }

    ${breakpoints.up('xxxl')} {
      max-width: 1800px;
      font-size: 72px;
    }
  }

  h2 {
    font-size: 24px;

    ${breakpoints.up('sm')} {
      font-size: 30px;
    }

    ${breakpoints.up('md')} {
      font-size: 36px;
    }

    ${breakpoints.up('lg')} {
      font-size: 44px;
    }

    ${breakpoints.up('xxl')} {
      font-size: 50px;
    }

    ${breakpoints.up('xxxl')} {
      font-size: 54px;
    }
  }

  h3 {
    font-size: 16px;

    ${breakpoints.up('sm')} {
      font-size: 20px;
    }

    ${breakpoints.up('md')} {
      font-size: 24px;
    }

    ${breakpoints.up('lg')} {
      font-size: 28px;
    }

    ${breakpoints.up('xxl')} {
      font-size: 30px;
    }

    ${breakpoints.up('xxxl')} {
      font-size: 32px;
    }
  }
`;

const LogoImage = styled(Box)<{ logowidth?: number }>`
  position: absolute;
  width: ${(props) => (props.logowidth ? `${props.logowidth}px` : '100px')};
  height: auto;
  z-index: 1;

  &.top-left {
    top: 10px;
    left: 10px;
  }

  &.top-right {
    top: 10px;
    right: 10px;
  }

  &.bottom-left {
    bottom: 10px;
    left: 10px;
  }

  &.bottom-right {
    bottom: 10px;
    right: 10px;
  }
`;

const HeroTitleWrapper = styled(Box)``;

// TYPES
//-------

export type HeroImageProps = {
  anchor?: string;
  aspectratio?: string;
};
/**
 * Properties type for the Hero block
 */
export type HeroBlockProps = BlockComponentBaseProps & {
  url?: string;
  title?: string;
  image?: EpiImage;
  video?: EpiImage;
  textAlign?: ContentAnchor; // ContentAnchorSelectionFactory
  imageAnchor?: ContentAnchor; // ContentAnchorSelectionFactory
  titleColor?: 'default' | 'black' | 'white'; // ContrastColorSelectionFactory
  descriptionColor?: 'default' | 'black' | 'white'; // ContrastColorSelectionFactory
  description?: string;
  ctaButton1?: EpiLink;
  ctaButton2?: EpiLink;
  logo?: EpiImage;
  logoPosition?: LogoPosition;
  aspectratio?: AspectRatio;
  logoWidth?: number;
  titleSize?: 'large' | 'medium' | 'small';
  heroWidth?: 'fullscreen' | 'regular';
  backdrop?: boolean;
  hideOnMobile?: boolean;
};

/**
 * Hero block component
 */
export const HeroBlock = ({
  url,
  title,
  image,
  video,
  textAlign,
  imageAnchor,
  titleColor,
  descriptionColor,
  disableEditPanel = false,
  description,
  ctaButton1,
  ctaButton2,
  logo,
  logoPosition,
  aspectratio,
  logoWidth,
  titleSize,
  heroWidth,
  backdrop,
  hideOnMobile,
}: HeroBlockProps) => {
  const mainClass = classnames('hero-block', textAlign || 'center', heroWidth);
  const contentClass = classnames('hero-block-content', textAlign || 'center');
  const titleWrapperClass = classnames('hero-block-title-wrapper');
  const mediaClass = classnames('hero-block-media');
  const theme = useTheme();
  const isBreakpointMedium = useMediaQuery(theme.breakpoints.up('md'));
  const logoUrl = typeof logo === 'string' ? logo : logo?.url;
  const logoClass = classnames('hero-block-logo', logoPosition || 'top-right');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const getTitleVariant = (size: 'large' | 'medium' | 'small' | undefined) => {
    if (size === 'large') return 'h1';
    else if (size === 'medium') return 'h2';
    else if (size === 'small') return 'h3';
    else return 'h1';
  };

  // Image
  const imageUrl = typeof image === 'string' ? image : image?.url || '';
  const resizedImageUrl = getImageResizerUrl(imageUrl ?? '', { width: 2000 });
  const srcSet = `
  ${getImageResizerUrl(imageUrl, { width: 400 })} 400w, 
  ${getImageResizerUrl(imageUrl, { width: 800 })} 800w, 
  ${getImageResizerUrl(imageUrl, { width: 1200 })} 1200w
`;

  // Video
  const videoUrl = typeof video === 'string' ? video : video?.url;
  const videoType = typeof video !== 'string' ? video?.expanded?.mimeType : null;
  const content = (
    <HeroBlockWrapper
      className={mainClass}
      anchor={imageAnchor || 'top left'}
      aspectratio={aspectratio || '16 / 9'}
      sx={{
        backgroundColor: backdrop ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
        height: 'auto',
      }}
    >
      {logoUrl && (
        <LogoImage className={logoClass} logowidth={logoWidth}>
          <img src={logoUrl} alt='logo' loading='lazy' />
        </LogoImage>
      )}
      {video ? (
        <video
          poster={imageUrl}
          playsInline={true}
          autoPlay={true}
          muted={true}
          loop={true}
          className={mediaClass}
        >
          <source src={videoUrl} type={videoType} />
        </video>
      ) : (
        <picture>
          <source srcSet={resizedImageUrl} type='image/webp' />
          <img
            src={resizedImageUrl}
            srcSet={srcSet}
            className={mediaClass}
            alt='hero'
            loading='eager'
            sizes='(max-width: 600px) 400px, (max-width: 1200px) 800px, 1200px'
          />
        </picture>
      )}
      <HeroTitleWrapper className={titleWrapperClass}>
        <HeroBlockContent className={contentClass}>
          <Title
            variant={getTitleVariant(titleSize)}
            color={titleColor}
            className='hero-title fade-in-transition fast'
          >
            {title}
          </Title>

          <Title variant='h3' color={descriptionColor} className='no-dot'>
            {description}
          </Title>

          {!url && (ctaButton1 || ctaButton2) && (
            <Stack
              spacing={2}
              direction={isBreakpointMedium ? 'row' : 'column'}
              mt={isBreakpointMedium ? 4 : 2}
              flexWrap={'wrap'}
            >
              {ctaButton1 && (
                <Button variant='primary' href={ctaButton1?.href}>
                  {ctaButton1?.text}
                </Button>
              )}
              {ctaButton2 && (
                <Button variant='primary' href={ctaButton2?.href}>
                  {ctaButton2?.text}
                </Button>
              )}
            </Stack>
          )}
        </HeroBlockContent>
      </HeroTitleWrapper>
    </HeroBlockWrapper>
  );
  const Block = (
    <EditPanel
      disableEditPanel={disableEditPanel}
      properties={[
        'title',
        'titleSize',
        'titleColor',
        'textAlign',
        'image',
        'logo',
        'logoPosition',
        'logoWidth',
        'video',
        'imageAnchor',
        'url',
        'description',
        'descriptionColor',
        'ctaButton1',
        'ctaButton2',
        'aspectratio',
      ]}
    />
  );

  useEffect(() => {
    const preloadImage = new Image();
    preloadImage.src = resizedImageUrl;
  }, [resizedImageUrl]);

  if (isMobile && hideOnMobile) {
    return null;
  }

  return (
    <BlockContainer fullscreen>
      {Block}
      {url ? <SmartLink href={url}>{content}</SmartLink> : content}
    </BlockContainer>
  );
};
